import { DAY, YEAR_DAYS } from '@/sim/clock';
import type { SacredStage } from '@/sim/anchor';
import type { SereStage } from '@/world/sere';
import type { HudPerson } from './hud';

/**
 * What is true of the chosen thing *right now*, in words.
 *
 * The counterpart of `ui/lore.ts`, which says what has happened to a place, and
 * of `ui/lives.ts`, which does the whole of one mortal. This file answers the
 * card's second question — **what is happening?** — for everything else the
 * pointer can resolve, and it exists so that the answer is composed rather than
 * hard-coded per panel: one function per kind of subject, each returning a few
 * plain sentences, and the card shows whichever ones it was given.
 *
 * Every rule `lives.ts` keeps, this keeps, and for the same reason:
 *
 * **No quantity that has to be compared against another quantity.** A
 * condition is *weathered*, not `0.42`; a herd is *a good many head*, not `68`.
 * The moment the card prints `standing: 0.4812` the player has stopped
 * perceiving a world and started reading database rows, and every subsequent
 * design decision gets made about the number instead of the thing.
 *
 * **The world states, it does not explain.** A cold hearth is a cold hearth; it
 * is not "abandoned — the band left in year 88". And nothing here may name a
 * hand: law 10 means a divine act reaches every eye as *unexplained*, so an
 * aura is a place that is doing well for no reason anybody can give.
 *
 * Pure formatting. No simulation, no clock read, no store — callers pass what
 * they already have, which is what lets `test/follow.ts` exercise these against
 * a live world with nothing rendered.
 */

/** Counts as words. Past a handful nobody counts heads, they say how much. */
const many = (n: number): string =>
  n <= 1 ? 'one of them'
  : n <= 3 ? 'a few of them'
  : n <= 12 ? 'a dozen or so'
  : n <= 40 ? 'a good many head'
  : n <= 120 ? 'a great herd'
  : 'more than anyone would count';

/** World-years, said the way you would say them about a thing that is old. */
const yearsSince = (at: number, now: number): number =>
  Math.max(0, Math.floor((now - at) / (DAY * YEAR_DAYS)));

const ago = (at: number, now: number): string => {
  const y = yearsSince(at, now);
  return y <= 0 ? 'within the year'
    : y === 1 ? 'a year ago'
    : y < 12 ? `${y} years ago`
    : y < 60 ? 'a lifetime ago'
    : y < 200 ? 'generations ago'
    : 'longer ago than anyone here remembers';
};

/* ── growing things ────────────────────────────────────────────────────── */

/**
 * A plant, by what stage of its own life it is at and what ground it is
 * standing in. `attention.ts` has already named it and said how much of it
 * there is; this is the part a god has to stand still to notice.
 *
 * `stage` is the sere's, so a tree in a burn says the burn is still around it —
 * which is the one fact about a plant that the landscape cannot show you from
 * the air, because regrowth and untouched wood are the same green.
 */
export function plantNow(
  kind: string, height: number, opts: { stage?: SereStage | null; moisture?: number } = {},
): string[] {
  const out: string[] = [];
  const woody = kind === 'conifer' || kind === 'spreading' || kind === 'broadleaf'
    || kind === 'willow' || kind === 'columnar' || kind === 'palm';
  if (kind === 'snag') out.push('dead, and still standing');
  else if (woody) {
    out.push(height >= 18 ? 'old growth — it was here before anyone living'
      : height >= 8 ? 'full grown'
      : height >= 3 ? 'coming up'
      : 'a seedling, and most of these do not make it');
  }
  if (opts.stage) {
    out.push(opts.stage === 'bare' ? 'standing on ground that has not come back yet'
      : opts.stage === 'grass' ? 'standing in grass that grew back after a fire'
      : opts.stage === 'shrub' ? 'standing in scrub that grew back after a fire'
      : 'standing in wood that grew back after a fire');
  }
  const m = opts.moisture;
  if (m !== undefined && Math.abs(m) > 0.12) {
    out.push(m > 0 ? 'the ground here is wetter than it should be'
                   : 'the ground here is drier than it should be');
  }
  return out;
}

/**
 * The ground's own variety, when there is one — the strain under a plant,
 * said the way this file says everything: direction words, no digits, and
 * only what a god standing still could notice. The deviations arrive
 * normalised (`strainAbout`); the largest axis names the kind, a second
 * clause says how much of the ground grows it. `spread` is deliberately
 * never spoken: the invisible axis stays invisible here too.
 */
export function strainNow(
  o: { share: number; dy: number; dt: number; de: number } | null,
): string[] {
  if (!o) return [];
  const picks: Array<[number, string]> = [
    [o.dy, o.dy > 0 ? 'a generous kind — fuller than its neighbours'
                    : 'a meagre kind — the ground gets little out of it'],
    [o.dt, o.dt > 0 ? 'a hardy kind — it keeps its green when the rains fail'
                    : 'a tender kind — the first to feel a dry year'],
    [o.de, o.de > 0 ? 'a quick-burning kind — fire loves this ground'
                    : 'a kind the fire passes over'],
  ];
  picks.sort((a, b) => Math.abs(b[0]) - Math.abs(a[0]));
  const out = [picks[0][1]];
  out.push(o.share > 0.6 ? 'and most of this ground grows it'
                         : 'growing in among the ordinary sort');
  return out;
}

/* ── animals ───────────────────────────────────────────────────────────── */

const HERD_DOING: Record<string, string> = {
  grazer: 'at grass',
  browser: 'working the scrub',
  small_game: 'in and out of cover',
  predator: 'hunting this ground',
  scavenger: 'working over what is left',
  fish: 'holding in the current',
};

/**
 * A herd — the group, not whichever animal is nearest the middle of the frame.
 * What a god watching from above can actually see: how many, what they are
 * doing, and whether they are going anywhere.
 */
export function herdNow(
  species: string, count: number, opts: { spread?: number; leaving?: boolean } = {},
): string[] {
  const out = [`${many(count)}, ${HERD_DOING[species] ?? 'about their ground'}`];
  if (opts.spread !== undefined) {
    out.push(opts.spread > 140 ? 'spread out across the ground'
      : opts.spread < 45 ? 'close together' : 'loosely gathered');
  }
  if (opts.leaving) out.push('and the ground they are on is emptying — they are moving off it');
  return out;
}

/** One animal out of it. A body, never a stat block. */
export function animalNow(species: string, alone: boolean): string[] {
  const out: string[] = [];
  out.push(alone ? 'on its own, away from the others'
                 : `one of the ${species === 'predator' ? 'hunters' : 'herd'}`);
  if (species === 'predator') out.push('the rest of the ground knows where it is');
  return out;
}

/* ── what people made ──────────────────────────────────────────────────── */

/**
 * A roof, a hearth or a rack, by whether anyone is keeping it up. The 0.3 that
 * divides standing from fallen is `render/structures.ts`' own RUIN, and the
 * word for a cold hearth is *cold* — not *abandoned*, which is a conclusion.
 */
export function structureNow(
  type: string, condition: number, lit: boolean, opts: { people?: string } = {},
): string[] {
  const out: string[] = [];
  if (condition < 0.3) out.push('fallen in — nobody has mended it in a long time');
  else if (condition < 0.7) out.push('weathered, and still standing');
  else out.push('kept up');
  if (type === 'hearth') out.push(lit ? 'the fire is in' : 'the fire is out');
  if (opts.people) out.push(`the way the ${opts.people} build`);
  return out;
}

/** A way through, by how much travel has worn it. */
export function trailNow(strength: number): string[] {
  return [
    strength > 0.85 ? 'walked so long it is a road'
      : strength > 0.5 ? 'well walked' : 'faint, and going back to grass',
    strength <= 0.5 ? 'little goes along it now' : 'somebody keeps it open',
  ];
}

/** A thing lying on the ground, and whether it is going anywhere. */
export function litterNow(kind: string): string[] {
  return kind === 'log' ? ['going back into the ground', 'and it will burn']
    : kind === 'stick' ? ['dry enough to carry a fire']
    : kind === 'clump' ? ['holding the soil where it sits']
    : ['it has been here longer than anything growing around it'];
}

/* ── the god's own places ──────────────────────────────────────────────── */

const RAISED: Record<SacredStage, string> = {
  // `bare` is *nothing has been raised here*, which is a different fact from
  // *nobody knows it is here* — and the card was saying the second one. See
  // `anchorNow`: a stone two bands had walked to still read "nobody has found
  // it yet", one line above "2 bands know it is here". Two true readings that
  // contradict each other are worse than either alone, and the `bare` line was
  // the one saying something it does not know.
  bare: 'nothing has been raised here',
  footpath: 'feet have worn a way to it',
  offerings: 'they leave things here',
  cairn: 'they have raised a cairn over it',
  shelter: 'they have roofed it',
  shrine: 'it is a shrine now',
  temple: 'it is a temple now',
};

/**
 * One of your own stones (`sim/anchor.ts`).
 *
 * Two quantities that must never be confused, and the panel keeps them apart in
 * the same way the simulation does: RESONANCE is deity↔place and is what you
 * actually made, REGARD is people↔place and belongs to the mortals. Neither is
 * ever printed as a number — what a god can see is how strongly the place still
 * answers, and how many peoples know it is there.
 *
 * The aura line deliberately says what you did, not what it is worth: the whole
 * mechanic is that you find out by leaving and coming back.
 */
/** How long an aura has been burning, in the units a god would feel it. */
const held = (litAt: number, now: number): string => {
  const days = Math.max(0, (now - litAt) / DAY);
  return days < 14 ? 'it has hardly begun to take'
    : days < 90 ? 'it has been gathering a while now'
      : 'it has been burning here a long time';
};

export function anchorNow(
  a: {
    stage: SacredStage; resonance: number; visits: number;
    knownBy: readonly string[]; placedAt: number;
    aura?: { verb: string; spent: number; litAt: number };
  },
  now: number,
): string[] {
  const out: string[] = [];
  out.push(`you bound this ground ${ago(a.placedAt, now)}`);
  out.push(a.resonance > 0.8 ? 'and the place still answers as strongly as the day you did'
    : a.resonance > 0.45 ? 'and the place still answers'
    : 'and the place answers faintly now');
  // …and who has come across it, which for an unraised stone IS the reading:
  // *nobody has found it yet* is the honest line only when nobody has.
  if (a.knownBy.length > 1) {
    out.push(RAISED[a.stage]);
    out.push(`${a.knownBy.length} bands know it is here`);
  } else if (a.knownBy.length === 1) {
    out.push(RAISED[a.stage]);
    out.push('one band knows it is here');
  } else {
    out.push(a.stage === 'bare' ? 'nobody has found it yet' : RAISED[a.stage]);
  }
  if (a.aura) {
    out.push(a.aura.verb === 'weaken'
      ? 'something of you is here, and it is taking from this place'
      : 'something of you is here, working while you are away');
    /**
     * …AND WHAT IT HAS COST, which `AuraState.spent` has been accumulating
     * since auras were built and nothing has ever read.
     *
     * It is the one thing a god cannot find out any other way. An aura is
     * silent by design — it works while you are elsewhere, which is the whole
     * point of it — so the power it has quietly taken is invisible at exactly
     * the moment it matters most. `aura.ts`'s own docstring says three stones
     * consume a god; this is that warning, said in the place where the god can
     * still do something about it.
     *
     * Bands are fractions of a WHOLE god, because that is the unit the player
     * actually feels: the purse is 0..1 and a day of regeneration is 0.02.
     * A tenth is several days of everything you are.
     */
    out.push(a.aura.spent >= 0.35
      ? 'and it has taken more of you than you may have meant to give'
      : a.aura.spent >= 0.1 ? 'and it has taken a real part of you by now'
        : 'and it has cost you little yet');
    /**
     * …and HOW LONG, which is `litAt` — written once when the aura was
     * kindled and, until now, read by nothing anywhere in the repository.
     *
     * The pair is the point. A cost on its own says nothing about whether it
     * was worth it; a cost *and* a duration is the only way an upkeep
     * discount is legible at all — "a real part of you" over a fortnight and
     * over a decade are different sentences about the same number, and the
     * second one is a god who has bought `aura.upkeep` and is getting away
     * with it.
     *
     * Deliberately NOT `ago()`, which is the same file's phrase for how long
     * ago a thing happened and speaks in world-years. An aura is measured
     * against how long a god is likely to be away, which is a matter of days.
     */
    out.push(held(a.aura.litAt, now));
  }
  return out;
}

/* ── the world itself ──────────────────────────────────────────────────── */

/** How wet the air is doing, said as weather rather than as a field value. */
const SKY = (rain: number, overcast: number): string | null =>
  rain > 0.45 ? 'rain is falling on it'
  : rain > 0.1 ? 'there is rain in the air'
  : overcast > 0.6 ? 'under a grey lid'
  : null;

/**
 * Bare ground, or a whole landform. What country this is, what the season is
 * doing to it, and what is standing on it — the questions a god flying over a
 * hillside would actually ask before deciding to care about it.
 */
export function groundNow(
  site: { biome: string; height: number; rain: number },
  opts: {
    stage?: SereStage | null; moisture?: number; biomass?: number;
    rainNow?: number; overcast?: number; burning?: boolean;
  } = {},
): string[] {
  const out: string[] = [];
  if (opts.burning) out.push('it is burning');
  if (opts.stage) {
    out.push(opts.stage === 'bare' ? 'burnt ground — nothing has come back yet'
      : opts.stage === 'grass' ? 'grass over a burn'
      : opts.stage === 'shrub' ? 'scrub where the wood was'
      : 'young wood, grown back since the fire');
  }
  const b = opts.biomass;
  if (b !== undefined && Math.abs(b) > 0.12) {
    out.push(b > 0 ? 'more grows here than the country would give it'
                   : 'less grows here than the country would give it');
  }
  const m = opts.moisture;
  if (m !== undefined && Math.abs(m) > 0.12) {
    out.push(m > 0 ? 'and the ground is wet' : 'and the ground is dry');
  }
  const sky = SKY(opts.rainNow ?? 0, opts.overcast ?? 0);
  if (sky) out.push(sky);
  if (out.length === 0) {
    out.push(site.rain > 0.62 ? 'wet country'
      : site.rain < 0.28 ? 'dry country'
      : site.height > 900 ? 'high country' : 'ordinary ground, and nothing is happening on it');
  }
  return out;
}

/* ── a life ────────────────────────────────────────────────────────────── */

/**
 * One mortal, out of what `ui/lives.ts` already said about them. Kept here
 * rather than folded into `describe()` because the card wants the same three
 * sentences every other subject gives it, and `HudPerson` is the richer record
 * the memory section also reads.
 */
export const personNow = (p: HudPerson): string[] =>
  [p.doing, p.where, `${p.condition} · ${p.temper}`];

/* ── why this god might care ───────────────────────────────────────────── */

/**
 * The card's last section: what the world holds apart about this thing, and
 * what of it is the god's own doing.
 *
 * `standing` is `world/things.ts`' signed significance, and it is the one place
 * the interface may say *something is different about this one* — it carries no
 * hand and cannot (`ThingMark` holds no `godId`), so a promoted tree reads as
 * remarkable to everybody and explained to nobody. That gap is law 10, and it
 * is where religion comes from.
 */
export function significanceOf(
  mark: { standing: number; acts: number; since: number } | null,
  now: number,
  promoteAt: number,
): string[] {
  if (!mark || Math.abs(mark.standing) < promoteAt) return [];
  const out: string[] = [];
  out.push(mark.standing > 0
    ? 'nothing about this one is ordinary any more'
    : 'something went wrong with this one, and it never came right');
  const y = yearsSince(mark.since, now);
  if (y >= 1) out.push(`it has been apart from the others for ${y === 1 ? 'a year' : `${y} years`}`);
  if (mark.acts > 1) {
    out.push(mark.acts <= 2 ? 'you have meant it twice'
      : mark.acts <= 5 ? 'you have meant it a few times'
      : mark.acts <= 20 ? 'you have meant it many times'
      : 'you have meant it over and over');
  } else if (mark.acts === 1) out.push('you meant it once');
  return out;
}
